import { z } from 'zod';
import { skinAssetMetadataSchema } from './asset';
import { SKIN_ENTRY_STATUSES } from './manifest';
import { skinVfxDescriptorSchema } from './vfx';

/**
 * What the browser is allowed to be told about a skin (ADR 0041).
 *
 * ## Why this is not `SkinManifest`
 *
 * `SkinManifest` is the AUTHORING record: it lives in `assets-source`, it is
 * what the validator reads, and it carries authoring notes. This is what the
 * asset pipeline EMITS for the bundle to fetch - the same fields, minus the
 * prose, plus a resolved URL the loader can hand to Phaser.
 *
 * Keeping them separate is the point. The `skin.json` on disk stays the single
 * source of every number; nothing is retyped into a second TypeScript catalogue
 * that could drift from it. This schema exists so what crosses into the runtime
 * is validated once more, at the boundary, by the same contract that produced
 * it.
 *
 * ## Fail closed
 *
 * A malformed sidecar throws. A skin with a hash that no longer matches its
 * file never reaches here, because the pipeline refuses to emit it. There is no
 * partial mode in which a skin is drawn from metadata nobody checked.
 */

export const SKIN_RUNTIME_MANIFEST_VERSION = 1;

/** Where the emitted sidecar lives, relative to the served asset root. */
export const SKIN_RUNTIME_MANIFEST_FILE = 'skin-manifest.json';

export const skinRuntimeEntrySchema = z
  .object({
    definitionId: z.string().min(3).max(64),
    status: z.enum(SKIN_ENTRY_STATUSES),
    contentVersion: z.int().min(1),
    /** The authoring metadata, unchanged and re-validated. */
    asset: skinAssetMetadataSchema,
    /** The descriptor, unchanged and re-validated. */
    vfx: skinVfxDescriptorSchema,
    /**
     * Path under the asset root, as the pipeline actually wrote it.
     *
     * Derived by the pipeline from `asset.file`, never from anything a user
     * typed: the loader must not be able to be pointed at an arbitrary path.
     */
    file: z.string().regex(/^skins\/[a-z0-9][a-z0-9._-]*\.png$/, 'a skin file lives under skins/'),
    /**
     * Where body sparkle may be born, as fractions of this skin's own texture.
     *
     * ## Why the browser is handed points rather than a picture (gate V27A)
     *
     * The overlay used to scatter its particles in a disc around a hull-local
     * offset, and a ship's PNG is a rectangle that is 45% to 55% transparent -
     * so a good share of every skin's "body" sparkle was born in the void
     * beside the hull. It read as loose glitter rather than as energy on a
     * machine.
     *
     * Reading the alpha is the expensive part and it is the same answer every
     * frame, so it happens ONCE, at build time. What ships is a short list of
     * `[0,1]` fractions; the renderer multiplies them by the drawn size and the
     * rotation the hull sprite already got. No texture is sampled, no canvas is
     * read and no array is allocated while a game is running.
     *
     * Empty is legal and means "this skin has no body sparkle to place" - a
     * descriptor with no overlay, or art the erosion left nothing of.
     *
     * It is presentation. Nothing that resolves a hit, a hitbox, a position or
     * a reward can reach it.
     */
    bodyPoints: z
      .array(z.object({ x: z.number().gt(0).lt(1), y: z.number().gt(0).lt(1) }).strict())
      .max(256)
      .default([]),
  })
  .strict();

export type SkinRuntimeEntry = z.infer<typeof skinRuntimeEntrySchema>;

export const skinRuntimeManifestSchema = z
  .object({
    manifestVersion: z.literal(SKIN_RUNTIME_MANIFEST_VERSION),
    /** Base URL the `file` paths resolve against, like the asset manifest. */
    baseUrl: z.string(),
    /**
     * Whether this sidecar was built with unfinished content in it.
     *
     * The production build never asks for draft skins, so a sidecar it produced
     * has this false and carries only finished entries. The flag is recorded
     * rather than inferred so a test can assert on the artefact itself.
     */
    includesDraft: z.boolean(),
    /** Sorted by `definitionId`, so two builds of the same tree are identical. */
    entries: z.array(skinRuntimeEntrySchema),
  })
  .strict()
  .superRefine((manifest, ctx) => {
    const ids = manifest.entries.map((entry) => entry.definitionId);
    if (ids.some((id, index) => index > 0 && (ids[index - 1] as string) >= id)) {
      ctx.addIssue({
        code: 'custom',
        path: ['entries'],
        message: 'entries must be sorted by definitionId and unique',
      });
    }
    if (!manifest.includesDraft && manifest.entries.some((entry) => entry.status !== 'ready')) {
      /*
       * The guarantee that matters for production. A sidecar that says it holds
       * no draft content and then holds some is a build that leaked unfinished
       * art, and it fails here rather than in a browser.
       */
      ctx.addIssue({
        code: 'custom',
        path: ['entries'],
        message: 'a manifest that excludes drafts must contain only ready entries',
      });
    }
  });

export type SkinRuntimeManifest = z.infer<typeof skinRuntimeManifestSchema>;

/** An empty sidecar. What a production build with no finished skin emits. */
export function emptySkinRuntimeManifest(baseUrl: string): SkinRuntimeManifest {
  return {
    manifestVersion: SKIN_RUNTIME_MANIFEST_VERSION,
    baseUrl,
    includesDraft: false,
    entries: [],
  };
}

/**
 * The Phaser texture key for one skin's hull art.
 *
 * Derived from the definition id rather than from the file name, for the same
 * reason nothing else here is inferred from a file name: renaming the PNG must
 * not change what the runtime calls it. Prefixed so it can never collide with
 * `ship1`..`ship20` or with a generated dash texture.
 */
export function skinTextureKey(definitionId: string): string {
  return `skin:${definitionId}`;
}
